import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fleetAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, Truck, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';

export default function FleetSelect({ kind = 'vehicle', value, onChange, onSelect, placeholder, className = '' }) {
  const { user } = useAuth();
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  const api = kind === 'vehicle' ? fleetAPI.vehicles : fleetAPI.drivers;
  const canCreate = ['admin', 'owner', 'super_admin'].includes(user?.role) || (user?.permissions || []).includes(`${kind}:create`);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await api.list({ search: q || undefined, status: 'active' });
      setResults(res.data?.[kind === 'vehicle' ? 'vehicles' : 'drivers'] || []);
      setOpen(true);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, [api, kind]);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    onChange && onChange(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 150);
  };

  const select = (item) => {
    const label = kind === 'vehicle' ? item.vehicle_number : item.name;
    setQuery(label);
    onChange && onChange(label);
    onSelect && onSelect(item);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid={`${kind}-select`}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input type="text" value={query} onChange={handleInput} onFocus={() => doSearch(query)}
          placeholder={placeholder || (kind === 'vehicle' ? 'Search vehicle no / owner / type...' : 'Search driver name / mobile / license...')}
          className={`form-input pl-8 ${className}`} data-testid={`${kind}-select-input`} autoComplete="off" />
        {loading && <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />}
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-56 overflow-y-auto" data-testid={`${kind}-select-dropdown`}>
          {results.map((item) => (
            <button key={item[`${kind}_id`]} type="button" onClick={() => select(item)}
              className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm border-b border-slate-50 last:border-0"
              data-testid={`${kind}-option-${item[`${kind}_id`]}`}>
              <div className="flex items-center gap-2">
                {kind === 'vehicle' ? <Truck className="w-3.5 h-3.5 text-purple-500 shrink-0" /> : <User className="w-3.5 h-3.5 text-purple-500 shrink-0" />}
                <span className="font-medium text-slate-800">{kind === 'vehicle' ? item.vehicle_number : item.name}</span>
              </div>
              <div className="flex gap-2 mt-0.5 text-[11px] text-slate-400 ml-5">
                {kind === 'vehicle' ? (<>
                  {item.vehicle_type && <span>{item.vehicle_type}</span>}
                  {item.owner_name && <span>Owner: {item.owner_name}</span>}
                </>) : (<>
                  {item.mobile && <span>{item.mobile}</span>}
                  {item.license_number && <span>DL: {item.license_number}</span>}
                </>)}
              </div>
            </button>
          ))}
          {results.length === 0 && !loading && (
            <div className="px-3 py-3 text-sm text-slate-400 text-center">No {kind}s found{query ? ` matching "${query}"` : ''}</div>
          )}
          {canCreate && (
            <button type="button" onClick={() => { setShowQuickAdd(true); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 text-sm font-medium text-emerald-700 flex items-center gap-2 border-t border-slate-100 sticky bottom-0 bg-white"
              data-testid={`${kind}-quick-add-btn`}>
              <Plus className="w-3.5 h-3.5" /> Add new {kind}
            </button>
          )}
        </div>
      )}
      {showQuickAdd && <QuickAddModal kind={kind} initial={query} onClose={() => setShowQuickAdd(false)} onCreated={select} />}
    </div>
  );
}

function QuickAddModal({ kind, initial, onClose, onCreated }) {
  const isVehicle = kind === 'vehicle';
  const [form, setForm] = useState(isVehicle
    ? { vehicle_number: initial || '', vehicle_type: '', capacity: '', owner_name: '', owner_mobile: '' }
    : { name: initial || '', mobile: '', license_number: '' });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const save = async () => {
    const required = isVehicle ? form.vehicle_number : form.name;
    if (!required?.trim()) { toast.error(isVehicle ? 'Vehicle number is required' : 'Driver name is required'); return; }
    setSaving(true);
    try {
      const api = isVehicle ? fleetAPI.vehicles : fleetAPI.drivers;
      const res = await api.create(form);
      if (res.data?.warning) toast.warning(res.data.warning);
      toast.success(`${isVehicle ? 'Vehicle' : 'Driver'} added`);
      onCreated(res.data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${kind}-quick-add-modal`}>
        <DialogHeader><DialogTitle>Quick Add {isVehicle ? 'Vehicle' : 'Driver'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isVehicle ? (<>
            <div><label className="form-label">Vehicle Number *</label><input className="form-input" value={form.vehicle_number} onChange={e => u('vehicle_number', e.target.value.toUpperCase())} placeholder="DL01AB1234" data-testid="qa-vehicle-number" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Vehicle Type</label><input className="form-input" value={form.vehicle_type} onChange={e => u('vehicle_type', e.target.value)} placeholder="20FT Truck" data-testid="qa-vehicle-type" /></div>
              <div><label className="form-label">Capacity</label><input className="form-input" value={form.capacity} onChange={e => u('capacity', e.target.value)} placeholder="9 Ton" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Owner Name</label><input className="form-input" value={form.owner_name} onChange={e => u('owner_name', e.target.value)} /></div>
              <div><label className="form-label">Owner Mobile</label><input className="form-input" value={form.owner_mobile} onChange={e => u('owner_mobile', e.target.value)} /></div>
            </div>
          </>) : (<>
            <div><label className="form-label">Driver Name *</label><input className="form-input" value={form.name} onChange={e => u('name', e.target.value)} data-testid="qa-driver-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Mobile</label><input className="form-input" value={form.mobile} onChange={e => u('mobile', e.target.value)} data-testid="qa-driver-mobile" /></div>
              <div><label className="form-label">License No.</label><input className="form-input" value={form.license_number} onChange={e => u('license_number', e.target.value.toUpperCase())} /></div>
            </div>
          </>)}
          <p className="text-[11px] text-slate-400">Full details can be added later from the {isVehicle ? 'Vehicles' : 'Drivers'} master page.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid={`qa-save-${kind}`}>{saving ? 'Saving...' : 'Save & Select'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
